import { useMemo } from 'react';
import { styled } from '@mui/system';
import { format, isValid } from 'date-fns';
import orderBy from 'lodash.orderby';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { LocationsIcon } from 'assets/images/icons';
import { SurveysIcon } from 'assets/images/sidebar';
import { Typography } from '@components/Typography';
import MediaGallery from '@components/MediaGallery';
import ResponseItem from '@components/ResponseItem';
import { InfoChip } from '@components/ResponseItem/styles';
import {
    CustomFieldType,
    INCLUDE_COVER_MANAGER,
    INCLUDE_RECOVERY_FORM,
    SurveyResponseSource,
} from '@constants/feedbacks';
import { ProfileName } from '@constants/integrations';
import { I18N_DATE_FORMAT, formatDateOrEmpty } from '@helpers/dates';
import { reviewTagLabel } from '@helpers/reviewTags';
import { useTranslation } from '@hooks';
import { feedbacksService } from '@services';
import FeedbackToolbar from './FeedbackToolbar';
import { CoverManagerIcon } from 'assets/images/integrations';
import LinkedReview from './LinkedReview';

const BodyTextTypography = styled(Typography)({
    whiteSpace: 'pre-line',
});

const StyledSurveysIcon = styled(SurveysIcon)({
    '& path': {
        fill: 'var(--primary-main)',
        fillOpacity: 1,
    },
});

const FeedbackBody = ({ item, language }) => {
    const { t } = useTranslation();
    const emptyTextKey =
        item?.source === SurveyResponseSource.RecoveryForm
            ? 'FeedbackResponses.contactDetailsOnly'
            : 'FeedbackResponses.onlyRating';
    const hasPhotos = item?.photos?.length > 0;
    const media = useMemo(
        () =>
            item?.photos?.map(photo => ({
                thumbnailUrl: photo.url,
                refresh: () => feedbacksService.getPhotoLink(photo.id).then(link => link.url),
            })),
        [item?.photos],
    );
    return (
        <div style={{ marginTop: '16px' }}>
            {item?.aiReviewTags?.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '12px',
                    }}
                >
                    <Typography variant="subtitle2" color="textPrimary">
                        {t('FeedbackResponses.aiReviewTags')}:
                    </Typography>
                    {item.aiReviewTags.map(tag => (
                        <InfoChip key={tag}>
                            <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>
                                {reviewTagLabel(tag, t)}
                            </Typography>
                        </InfoChip>
                    ))}
                </div>
            )}
            {(item?.customFields?.length > 0 || item?.answers?.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                    {item?.customFields?.map(
                        field =>
                            field?.value && (
                                <div key={field?.name} style={{ display: 'flex', marginBottom: '5px' }}>
                                    <Typography variant="subtitle2" color="textPrimary">
                                        {field?.name}:
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ marginLeft: '5px' }}>
                                        {field?.type === CustomFieldType.Date &&
                                        field?.value &&
                                        isValid(new Date(field?.value))
                                            ? format(new Date(field?.value), 'd MMM yyyy', {
                                                  locale: I18N_DATE_FORMAT[language],
                                              })
                                            : field?.value}
                                    </Typography>
                                </div>
                            ),
                    )}
                    {orderBy(item?.answers, ['group'], ['asc']).map(answer => (
                        <div key={answer?.title} style={{ display: 'flex', marginBottom: '5px' }}>
                            <Typography variant="subtitle2" color="textPrimary">
                                {answer?.title}:
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ marginLeft: '5px' }}>
                                {answer?.value || answer?.answer}
                                {answer?.otherText ? `: ${answer.otherText}` : ''}
                            </Typography>
                        </div>
                    ))}
                </div>
            )}

            {(item?.text || !hasPhotos) && (
                <BodyTextTypography
                    variant="body2"
                    color="textSecondary"
                    style={{
                        color: item?.text ? 'inherit' : 'var(--text-tertiary)',
                    }}
                >
                    {item?.text || t(emptyTextKey)}
                </BodyTextTypography>
            )}
            <MediaGallery media={media} />
        </div>
    );
};

const SurveyChip = ({ surveyName, isCoverManager, onClick }) => {
    if (!surveyName) return null;
    return (
        <InfoChip onClick={onClick}>
            {isCoverManager ? <CoverManagerIcon height={16} width={16} /> : <StyledSurveysIcon />}
            <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>
                {surveyName}
            </Typography>
        </InfoChip>
    );
};

const LocationChip = ({ locationName, onClick }) => {
    if (!locationName) return null;
    return (
        <InfoChip onClick={onClick}>
            <LocationsIcon />
            <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>
                {locationName}
            </Typography>
        </InfoChip>
    );
};

const FeedbackItem = ({
    item,
    index,
    language,
    handleOpenTicket,
    onClickContactDetails,
    onFilterByLocation,
    onFilterBySurvey,
}) => {
    const { t } = useTranslation();

    const isCoverManager = item?.type === ProfileName.CoverManager;
    const isRecoveryForm = item?.source === SurveyResponseSource.RecoveryForm;
    const date = formatDateOrEmpty(item?.createdAt, 'd MMM yyyy', language);

    const surveyChipName = () => {
        if (isRecoveryForm) return t('common.recoveryForm');
        if (isCoverManager) return 'CoverManager';
        return item?.surveyName;
    };

    return (
        <ResponseItem
            key={item?.id || `feedback-${index}`}
            index={index}
            firstName={item?.firstName}
            lastName={item?.lastName}
            phoneNumber={item?.phoneNumber}
            email={item?.email}
            contactExternalId={item?.contactExternalId}
            viewed={item?.viewed}
            rating={item?.rating}
            date={date}
            chips={
                <>
                    <SurveyChip
                        surveyName={surveyChipName()}
                        isCoverManager={isCoverManager}
                        onClick={() =>
                            onFilterBySurvey(
                                item?.surveyTemplateId,
                                isRecoveryForm ? INCLUDE_RECOVERY_FORM : isCoverManager ? INCLUDE_COVER_MANAGER : null,
                            )
                        }
                    />
                    <LocationChip
                        locationName={item?.internalName}
                        onClick={() => onFilterByLocation(item?.locationId)}
                    />
                </>
            }
            body={
                <>
                    <FeedbackBody item={item} language={language} />
                    {item?.review && <LinkedReview review={item.review} language={language} />}
                </>
            }
            footer={<FeedbackToolbar data={item} onClickTicket={handleOpenTicket} />}
            contactDetailsLink={
                item?.contactId ? (
                    <Typography
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            color: 'var(--primary-main)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                        onClick={() => onClickContactDetails(item?.contactId, 'view')}
                    >
                        <PersonOutlineRoundedIcon sx={{ fontSize: 'inherit' }} />
                        {t('InboxPage.viewContact')}
                    </Typography>
                ) : null
            }
        />
    );
};

FeedbackItem.displayName = 'FeedbackItem';

export default FeedbackItem;
